import * as React from "react";
import { CheckCircle, Zap } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";

interface ProjectPaymentPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubscribe: () => void;
  isProcessing?: boolean;
}

const TABLE_DATA = [
  {
    type: "row",
    category: "Projects",
    items: [{ label: "Unlimited Nos.", status: "Ready" }],
  },
  {
    type: "row",
    category: "Nodes & Edges",
    items: [
      { label: "Unlimited Nos.", status: "Ready" },
      { label: "Mobile", status: "Ready" },
      { label: "Web", status: "Ready" },
      { label: "Desktop", status: "Ready" },
      { label: "Service", status: "Ready" },
      { label: "Database", status: "Ready" },
      { label: "Landing Page", status: "Ready" },
      { label: "Blog", status: "Ready" },
      { label: "Auth Service", status: "Ready" },
      { label: "LLM", status: "Soon" },
      { label: "Worker", status: "Soon" },
      { label: "Cache", status: "Soon" },
      { label: "Object Storage", status: "Soon" },
      { label: "Message Queue", status: "Soon" },
      { label: "Monitoring", status: "Soon" },
      { label: "Managed DB", status: "Soon" },
    ],
  },
  {
    type: "row",
    category: "Agents",
    items: [
      { label: "Architect", status: "Ready" },
      { label: "Developer", status: "Soon" },
      { label: "Code Reviewer", status: "Soon" },
    ],
  },
  {
    type: "row",
    category: "Canvas",
    items: [
      { label: "Architecting", status: "Ready" },
      { label: "Interface", status: "Soon" },
      { label: "Workflows", status: "Soon" },
      { label: "Agents", status: "Soon" },
    ],
  },
  {
    type: "row",
    category: "Full Stack Suite",
    items: [
      { label: "Node.js", status: "Ready" },
      { label: "React", status: "Ready" },
      { label: "Next.js", status: "Ready" },
      { label: "Astro", status: "Ready" },
      { label: "Postgres", status: "Ready" },
      { label: "MySql", status: "Ready" },
      { label: "MongoDB", status: "Ready" },
      { label: "Hono", status: "Ready" },
      { label: "Auth (Arctic)", status: "Ready" },
      { label: "ShadcnUI", status: "Ready" },
      { label: "Mobile (React Native)", status: "Ready" },
      { label: "Desktop (Electron)", status: "Ready" },
      { label: "Python", status: "Soon" },
      { label: "Angular", status: "Soon" },
      { label: "Vue", status: "Soon" },
      { label: "tRPC", status: "Soon" },
      { label: "Redis", status: "Soon" },
    ],
  },
  {
    type: "row",
    category: "Cloud",
    items: [
      { label: "DigitalOcean", status: "Ready" },
      { label: "AWS", status: "Soon" },
      { label: "GCP", status: "Soon" },
      { label: "Azure", status: "Soon" },
    ],
  },
  {
    type: "row",
    category: "Consultation",
    items: [
      { label: "Free 15-min Call", status: "Ready" },
      { label: "Hire Developers", status: "Ready" },
      { label: "Email Support", status: "Ready" },
    ],
  },
];

export function ProjectPaymentPopup({
  open,
  onOpenChange,
  onSubscribe,
  isProcessing = false,
}: ProjectPaymentPopupProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[950px] bg-background border-none shadow-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-2 text-center">
          <DialogTitle className="text-4xl font-bold text-center">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-pink-600">
              Scout Pack
            </span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-0 text-center">
            Unlock the max power of Buzooka with the Scout Pack
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4">
          <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm mb-6">
            <Table>
              <TableBody>
                {TABLE_DATA.map((row, index) => {
                  const readyItems = row.items?.filter(item => item.status === "Ready") || [];
                  const soonItems = row.items?.filter(item => item.status === "Soon") || [];

                  return (
                    <TableRow key={index} className="hover:bg-muted/50 border-border">
                      <TableCell className="py-3 px-4 align-top font-bold text-[10px] uppercase tracking-tight text-muted-foreground border-r border-border">
                        {row.category}
                      </TableCell>
                      <TableCell className="py-2 px-2 align-top border-r border-border">
                        <div className="flex flex-wrap gap-1.5 justify-start">
                          {readyItems.map((item, i) => (
                            <div 
                              key={i} 
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-medium whitespace-nowrap transition-colors bg-secondary/50 border-border text-muted-foreground"
                            >
                              <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                              <span className="leading-none">{item.label}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-2 align-top">
                        <div className="flex flex-wrap gap-1.5 justify-start">
                          {soonItems.map((item, i) => (
                            <div 
                              key={i} 
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-medium whitespace-nowrap transition-colors bg-secondary/50 border-border text-muted-foreground"
                            >
                              <span className="leading-none">{item.label}</span>
                              <span className="text-[8px] font-bold uppercase ml-0.5 whitespace-nowrap leading-none text-amber-500/80">Soon</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
            <div className="flex flex-col lg:flex-row items-center lg:items-center justify-between bg-card p-6 rounded-2xl shadow-sm border border-border gap-8 max-w-xl mx-auto">
              <div className="flex flex-col items-center lg:items-center gap-4 shrink-0">
                <div className="flex flex-col items-center lg:items-center gap-1.5">
                  <div className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-extrabold bg-green-500/10 text-green-600 dark:text-green-400">
                    Prelaunch: Save ~80% Today
                  </div>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
                    On Annual commitment
                  </p>
                </div>

                <div className="flex flex-col items-center lg:items-center">
                  <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                    <span className="text-base text-muted-foreground/50 line-through font-medium">
                      $49
                    </span>
                    <span className="text-5xl font-bold text-foreground tracking-tight">
                      $9
                    </span>
                    <span className="text-sm font-semibold text-muted-foreground tracking-tight">/ month</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col w-full lg:w-auto gap-2 shrink-0">
                <div className="flex flex-col items-center gap-0.5">
                  <p className="text-sm font-normal text-muted-foreground tracking-tight text-center">
                    Billed as <span className="font-bold text-lg text-foreground">$108</span> per year
                  </p>
                </div>
                <Button
                  onClick={onSubscribe}
                  disabled={isProcessing}
                  className="px-10 w-full lg:min-w-[220px] h-12 text-base shadow-xl transition-all rounded-xl font-bold"
                >
                  {/* Changed text to explicitly state the amount being paid right now */}
                  {isProcessing ? "Processing..." : "Subscribe Now"}
                  {!isProcessing && <Zap className="w-3.5 h-3.5 ml-2 fill-yellow-400 text-yellow-400" />}
                </Button>
                  <p className="text-xs text-center text-muted-foreground font-medium tracking-tight">
                    Full access for the next 12 months
                  </p>
              </div>
            </div>
        </div>

        <div className="px-6 pb-6 pt-2">
          <p className="text-[11px] text-center text-muted-foreground/80">
            Secure checkout via Lemon Squeezy. 30-day money-back guarantee. Read
            our{" "}
            <a
              href="https://buzooka.in/refund"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground transition-colors"
            >
              refund policy
            </a>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
